import {
  Box,
  ButtonVariants,
  CandlesticksChart,
  FormControlSizes,
  InlineFormattedNumber,
  MarketTypeEnum,
  NumberVariants,
  Text,
  ToggleGroup,
  notEmpty,
  useMarketAccountsContext,
  useMarketsContext,
  useOHLCData,
  useObservable,
  useObservableValue,
  type Market,
  type MarketAccount,
  type OHLCData,
  type ResolutionEnumNoPrefix,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { isEqual, uniq } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { map } from 'rxjs';
import styled from 'styled-components';
import { ErrorBoundary } from '../ErrorBoundary';

interface CandleStickSlideProps {
  security: Security;
  markets: string[];
  marketAccounts: string[];
  resolution: ResolutionEnumNoPrefix;
  onChangeResolution: (resolution: ResolutionEnumNoPrefix) => void;
}

const ONE_MINUTE = 60 * 1000;

export function CandleStickSlide({
  security,
  markets,
  marketAccounts,
  resolution,
  onChangeResolution,
}: CandleStickSlideProps) {
  const symbol = security.Symbol;
  const { marketAccountsByName } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();
  const [marketsList, setMarketsList] = useState<string[]>(
    getMarkets(markets, marketAccounts, marketAccountsByName, marketsByName)
  );

  useEffect(() => {
    const newMarketsList = getMarkets(markets, marketAccounts, marketAccountsByName, marketsByName);
    setMarketsList(prev => {
      return isEqual(prev, newMarketsList) ? prev : newMarketsList;
    });
  }, [markets, marketAccounts, marketAccountsByName, marketsByName]);

  const { startDate, endDate, interval } = useMemo(() => {
    const endDate = new Date();
    let duration = 0;
    let interval = '';
    switch (resolution) {
      case '1M': {
        interval = '1 hour';
        duration = ONE_MINUTE * 60;
        break;
      }
      case '5M': {
        interval = '5 hours';
        duration = ONE_MINUTE * 5 * 60;
        break;
      }
      case '1H': {
        interval = '2 days';
        duration = ONE_MINUTE * 60 * 48;
        break;
      }
      case '4H': {
        interval = '12 days';
        duration = ONE_MINUTE * 60 * 4 * 48;
        break;
      }
      case '1D': {
        interval = '60 days';
        duration = ONE_MINUTE * 60 * 24 * 60;
        break;
      }
    }
    const startDate = new Date(Date.now() - duration);
    return { startDate, endDate, interval };
  }, [resolution]);
  const sub = useOHLCData({ symbol, markets: marketsList, startDate, endDate, resolution });

  const stats = useObservableValue(
    () =>
      sub.pipe(
        map(data => {
          const filteredData = data.filter(d => d.High !== '0' && d.Low !== '0' && d.Open !== '0' && d.Close !== '0');
          const length = resolution === '1M' ? 60 : 24;
          const periodData = filteredData.slice(-length);
          const first = periodData.slice(0)[0];
          const last = periodData.slice(-1)[0];
          return first && last
            ? {
                open: first.Open,
                close: last.Close,
              }
            : undefined;
        })
      ),
    [sub, resolution]
  );
  const { open, close } = stats ?? { open: undefined, close: undefined };

  const dataObservable = useObservable(() => {
    let isInitial = true;
    return sub.pipe(
      map(data => {
        const filteredData = filterZeroValues(data);
        if (filteredData.length > 0 && isInitial) {
          isInitial = false;
          return {
            series: filteredData ?? [],
            initial: true,
          };
        } else {
          return {
            series: filteredData.slice(-1) ?? [],
            initial: false,
          };
        }
      })
    );
  }, [sub]);
  const change = close && open ? Big(close).minus(open).div(Big(open)).times(100) : undefined;
  const variant = change?.gt(0) ? NumberVariants.Positive : change?.lt(0) ? NumberVariants.Negative : undefined;

  return (
    <ErrorBoundary>
      <CandleStickSlideWrapper>
        <ResolutionToggleBox>
          <ToggleGroup<ResolutionEnumNoPrefix>
            options={[
              { value: '1M', displayName: '1m', buttonVariant: ButtonVariants.Primary },
              { value: '5M', displayName: '5m', buttonVariant: ButtonVariants.Primary },
              { value: '1H', displayName: '1h', buttonVariant: ButtonVariants.Primary },
              { value: '4H', displayName: '4h', buttonVariant: ButtonVariants.Primary },
              { value: '1D', displayName: '1d', buttonVariant: ButtonVariants.Primary },
            ]}
            value={resolution}
            onChange={onChangeResolution}
            size={FormControlSizes.Small}
            data-testid="candlestick-resolution-toggle"
          />
        </ResolutionToggleBox>
        <Stats>
          Last <Text color="colorTextImportant">{interval}</Text>{' '}
          {variant === NumberVariants.Positive && <Text color="colorTextPositive">+</Text>}
          <InlineFormattedNumber number={change} variant={variant} increment="0.01" currency="%" round />
        </Stats>
        <CandlesticksChart
          key={resolution}
          dataObservable={dataObservable}
          width={278}
          height={160}
          compact
          disableInteraction
        />
      </CandleStickSlideWrapper>
    </ErrorBoundary>
  );
}

const Stats = styled(Box)`
  opacity: 1;
  transition: opacity 200ms ease;
  font-size: ${({ theme }) => theme.fontSizeSm}rem;
  margin-bottom: ${({ theme }) => -theme.spacing12}px;
  z-index: 10;
  pointer-events: none;
  position: relative;
`;

const ResolutionToggleBox = styled(Box)`
  position: absolute;
  top: 0;
  z-index: 10;
  opacity: 0;
  transition: opacity 200ms ease;
`;

const CandleStickSlideWrapper = styled(Box)`
  position: relative;

  &:hover {
    ${ResolutionToggleBox} {
      opacity: 1;
    }
    ${Stats} {
      opacity: 0;
    }
  }
`;
CandleStickSlideWrapper.defaultProps = {
  px: 'spacing16',
};

function filterZeroValues(data: OHLCData[]) {
  return data.filter(d => d.High !== '0' && d.Low !== '0' && d.Open !== '0' && d.Close !== '0');
}

function getMarkets(
  markets: string[],
  marketAccounts: string[],
  marketAccountsByName: Map<string, MarketAccount>,
  marketsByName: Map<string, Market>
) {
  return uniq(
    [
      ...markets,
      ...marketAccounts.map(ma => {
        const marketName = marketAccountsByName.get(ma)?.Market;
        if (marketName) {
          const market = marketsByName.get(marketName);
          return market?.Type === MarketTypeEnum.Exchange ? marketName : undefined;
        }
        return undefined;
      }),
    ].filter(notEmpty)
  );
}
